import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { LogIn } from 'lucide-react';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const { login, user } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();

  // If the visitor is already authenticated, bounce to home — no point in
  // showing the login form again.
  useEffect(() => {
    if (user) navigate('/', { replace: true });
  }, [user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email || !password) {
      toast.error(t('errors.fillAllFields'));
      return;
    }

    setLoading(true);
    try {
      await login(email, password);
      toast.success(t('auth.loginSuccess'));
      navigate('/');
    } catch (error) {
      console.error('Login error:', error);
      toast.error(error.response?.data?.detail || t('errors.loginError'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-container">
      <div className="auth-card">
        <div className="auth-header">
          <LogIn size={32} />
          <h1>{t('auth.loginTitle')}</h1>
          <p>{t('auth.enterCredentials')}</p>
        </div>

        <form onSubmit={handleSubmit} className="auth-form">
          <div className="form-group">
            <label htmlFor="email">{t('auth.email')}</label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="your@email.com"
              disabled={loading}
              data-testid="login-email"
            />
          </div>

          <div className="form-group">
            <label htmlFor="password">{t('auth.password')}</label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              disabled={loading}
              data-testid="login-password"
            />
          </div>

          <Button
            type="submit"
            disabled={loading}
            className="auth-submit-btn"
            data-testid="login-submit"
          >
            {loading ? t('auth.loggingIn') : t('auth.login')}
          </Button>
        </form>

        <div className="auth-footer">
          <p>
            <Link to="/forgot-password" style={{ color: '#667eea' }}>
              {t('auth.forgotPassword')}
            </Link>
          </p>
          <p style={{ marginTop: '0.5rem' }}>
            {t('auth.noAccount')}{' '}
            <Link to="/register" data-testid="register-link">
              {t('auth.registerLink')}
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;
